import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import Image from "../components/elements/Image";
import Logo from "../assets/images/logo-memory-doodle.svg";
import Button from "../components/forms/Button";
import { classNames } from "../helpers/classNames";
import Authentications from "../components/sections/front/sections/Authentications";
import UserDropdown from "./UserDropdown";
import { useSelector, useDispatch } from "react-redux";
import { resetStep, setIsComingFromNonUserRoute } from "../redux/slice/stepSlice";
import { setCoverImages, setImages } from "../redux/slice/sketchSlice";
import { saveStep } from "../services/stepService";
import { markStepAsVisited } from "../redux/slice/profileSlice";

const DefaultHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [, setScrolled] = useState(false);
  const [authenticationModal, setAuthenticationModal] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [cartItem, setCartItem] = useState(null);
  const [showCartDropdown, setShowCartDropdown] = useState(false);

  const [, setActiveSection] = useState("banner");
  const { session } = useSelector((state) => state?.authProvider);
  const Profile = useSelector((state) => state?.profile?.data);
  const { myBooks } = useSelector((state) => state?.profile);
  const { activeBookId } = useSelector((state) => state?.stepFlow);

  const menu = useMemo(
    () => [
      { _id: 1, name: "Home", link: "/" },
      { _id: 2, name: "How It Works", link: "/how-it-works" },
    ],
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 0;
      setScrolled(isScrolled);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
      let currentSection = null;
      for (const item of menu) {
        const section = document.getElementById(item.link);
        if (section) {
          const offsetTop = section.offsetTop - 96;
          const offsetBottom = offsetTop + section.offsetHeight;
          if (window.scrollY >= offsetTop && window.scrollY < offsetBottom) {
            currentSection = item.link;
            break;
          }
        }
      }
      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [menu]);

  // Close cart dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCartDropdown && !event.target.closest('.cart-dropdown-container')) {
        setShowCartDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCartDropdown]);

  // Get cart count and cart item from localStorage
  useEffect(() => {
    const getCartData = () => {
      try {
        const selectedPlan = localStorage.getItem("selectedPlan");
        if (selectedPlan) {
          const parsedPlan = JSON.parse(selectedPlan);
          // Count the number of keys in the object
          if (typeof parsedPlan === "object" && parsedPlan !== null && !Array.isArray(parsedPlan)) {
            setCartCount(1);
            setCartItem(parsedPlan);
          } else {
            setCartCount(0);
            setCartItem(null);
          }
        } else {
          setCartCount(0);
          setCartItem(null);
        }
      } catch (error) {
        console.error("Error parsing selectedPlan from localStorage:", error);
        setCartCount(0);
        setCartItem(null);
      }
    };

    // Initial load
    getCartData();

    // Listen for storage changes (when localStorage is updated from other tabs/windows)
    window.addEventListener("storage", getCartData);

    // Optional: Listen for custom event if you update localStorage in the same tab
    window.addEventListener("cartUpdated", getCartData);

    return () => {
      window.removeEventListener("storage", getCartData);
      window.removeEventListener("cartUpdated", getCartData);
    };
  }, []);

  const onLogoRedirection = () => {
    navigate("/");
  };

  const handleRemoveFromCart = () => {
    localStorage.removeItem("selectedPlan");
    setCartItem(null);
    setCartCount(0);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event("cartUpdated"));
    setShowCartDropdown(false);
  };

  const handleViewCart = () => {
    setShowCartDropdown(false);
    
    // Same logic as onColoringBookNavigate from UserDropdown
    const orderedBooks = myBooks?.filter((book) => !!book.isOrderPlaces) || [];
    
    if (!!activeBookId && !!orderedBooks.length) {
      dispatch(resetStep());
      dispatch(setCoverImages([]));
      dispatch(setImages([]));
      
      if (location.pathname === "/") dispatch(setIsComingFromNonUserRoute(true));
    } else if (!!activeBookId && myBooks?.length === 1) {
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: "Upload_Your_Cover_Photo",
      });
      dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Upload_Your_Cover_Photo' }));
    } else if (!activeBookId && location.pathname.includes("create-book") && myBooks?.length <= 1) {
      return;
    }
    
    navigate("/dashboard");
  };

  const toggleCartDropdown = () => {
    setShowCartDropdown(!showCartDropdown);
  };

  const closeCartDropdown = () => {
    setShowCartDropdown(false);
  };

  const isAuthenticated = !!Profile?.access_token;

  return (
    <>
      <header className={classNames("sticky z-50 top-0 w-full flex items-center transition-all duration-300 h-24 bg-white border-b border-md-pink-light-1")}>
        <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl mx-auto px-4 lg:px-20">
          <div className="flex items-center gap-4 sm:gap-16 3xl:gap-40">
            <div className={classNames("w-full max-w-60 transition-all duration-300 overflow-hidden h-14")}>
              <Link to={"/"} className="relative flex w-full h-full overflow-hidden" onClick={onLogoRedirection}>
                <Image src={Logo} alt={"Logo"} width={"100%"} height={"100%"} effect={"blur"} classes={"object-contain object-left"} />
              </Link>
            </div>
            <div className="ml-auto flex items-center gap-1 md:gap-2 sm:gap-4 2xl:gap-8">
              {!isAuthenticated && session?.status !== "loading" && (
                <div
                  className={classNames(
                    "flex xl:items-center gap-4 2xl:gap-8 fixed xl:relative top-0 xl:top-auto right-0 xl:right-auto h-full xl:h-auto w-80 xl:w-auto bg-white xl:bg-transparent flex-col xl:flex-row z-50 py-10 xl:py-0 transition-all duration-300 px-4 xl:px-0",
                    isMenuOpen ? "translate-x-0 xl:translate-x-0" : "translate-x-full xl:translate-x-0"
                  )}
                >
                  {menu?.map((item) => (
                    <div className="relative" key={item._id}>
                      <NavLink to={item.link} onClick={() => setMenuOpen(false)} className={({ isActive }) => classNames("px-3 block text-base 2xl:text-lg font-semibold transition-all duration-200 hover:text-blue", isActive ? "text-md-pink" : "text-md-dark")}>
                        {item.name}
                      </NavLink>
                    </div>
                  ))}
                </div>
              )}
              <div className={classNames("relative flex items-center gap-4")}>
                {!isAuthenticated && session?.status !== "loading" && (
                  <div className="hidden xl:flex">
                  </div>
                )}
                {!isAuthenticated && session?.status !== "loading" ? (
                  <></>
                ) : (
                  session?.status !== "loading" && (
                    <>
                      <div className="">
                        <UserDropdown user={Profile} />
                      </div>
                    </>
                  )
                )}
                { 
                  isAuthenticated && session?.status !== "loading" &&
                  <div 
                    className="relative cart-dropdown-container"
                  >
                    <Button
                      buttonIconPosition={"left"}
                      buttonClasses={"flex items-center justify-center w-10 aspect-square !h-10 !px-0 !rounded-full !text-slate-700 !bg-transparent !text-xl cursor-pointer"}
                      buttonLabelClasses={"text-white"}
                      buttonIcon={"fa-regular fa-bag-shopping"}
                      buttonFunction={toggleCartDropdown}
                    />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                        {cartCount}
                      </span>
                    )}

                    {/* Cart Dropdown */}
                    {showCartDropdown && (
                      <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50">
                        {cartItem ? (
                          <div className="p-4">
                            <div className="flex items-center justify-between mb-4 pb-3 border-b">
                              <h3 className="text-lg font-semibold text-gray-900">Shopping Cart</h3>
                              <button
                                onClick={closeCartDropdown}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Close cart"
                              >
                                <i className="fa-solid fa-xmark text-xl"></i>
                              </button>
                            </div>
                            
                            {/* Cart Item */}
                            <div className="flex gap-4 mb-4">
                              {/* Product Image */}
                              <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                                <img 
                                  src={cartItem.image} 
                                  alt={cartItem.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>

                              {/* Product Details */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                                  {cartItem.name}
                                </h4>
                                {cartItem.highLightTag > 0 && (
                                  <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded mb-2">
                                    {cartItem.highLightText}
                                  </span>
                                )}
                                <div className="flex items-center justify-between mt-2">
                                  <p className="text-lg font-bold text-md-pink">
                                    ${cartItem.planPricingData?.price}
                                  </p>
                                  <button
                                    onClick={handleRemoveFromCart}
                                    className="text-red-500 hover:text-red-700 text-sm font-medium transition-colors"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Subtotal */}
                            <div className="border-t pt-3 mb-3">
                              <div className="flex items-center justify-between text-base">
                                <span className="font-medium text-gray-700">Subtotal:</span>
                                <span className="font-bold text-gray-900">${cartItem.planPricingData?.price}</span>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={handleViewCart}
                                className="w-full bg-md-pink hover:bg-md-pink/90 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors"
                              >
                                Checkout
                              </button>
                              <button
                                onClick={closeCartDropdown}
                                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-4 rounded-lg transition-colors"
                              >
                                Continue Shopping
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center">
                            <div className="flex justify-end mb-2">
                              <button
                                onClick={closeCartDropdown}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Close cart"
                              >
                                <i className="fa-solid fa-xmark text-xl"></i>
                              </button>
                            </div>
                            <i className="fa-regular fa-bag-shopping text-4xl text-gray-300 mb-3"></i>
                            <p className="text-gray-500 font-medium mb-4">Your cart is empty</p>
                            <button
                              onClick={closeCartDropdown}
                              className="text-md-pink hover:text-md-pink/80 font-semibold text-sm transition-colors"
                            >
                              Continue Shopping
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
        <div className={classNames("fixed inset-0 bg-black/50 transition-all duration-300", isMenuOpen ? "opacity-100 xl:opacity-0 visible xl:invisible" : "opacity-0 xl:opacity-0 invisible xl:invisible")} onClick={() => setMenuOpen(false)} />
      </header>
      <Authentications authenticationModal={authenticationModal} setAuthenticationModal={setAuthenticationModal} />
    </>
  );
};

export default DefaultHeader;